import React from 'react';
import { useAppDispatch, useAppSelector, useMenu } from 'src/hooks';
import {
  Button,
  Grid,
  Drawer as MuiDrawer,
  Backdrop,
  LinearProgress,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Storage from 'src/utils/storage/storage';
import { useParams } from 'react-router-dom';
import { toggleDrawerView } from '../../store/planner-slice';
import DRAWER_VIEW_TYPE from '../../types/drawer-view-type';
import {
  createDrawerAPIPayload as createPayloadForOpportunitySubmission,
  createPayloadForOpportunitySubmissionV2
} from '../../utils/opportunity-utils';
import {
  resetDrawerDetails,
  submitOpportunitiesForProduct
} from '../../store/planner-slice-async-thunks';
import CommentBoxContainer from '../../../pricing-simulator/components/comment-box-container/comment-box-container';
import BulkCommentsDrawer from './comments-drawer';
import ProductDetailsDrawer from './product-details-drawer';
import { DrawerProductItem } from '../../types/ppg-sku-drawer.model';

const drawerWidth = 500;

const DrawerContent = styled('div')(({ theme }) => ({
  display: 'flex',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar
}));

const Drawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  zIndex: theme.zIndex.drawer + 2,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  backgroundColor: '#FBFBFB',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  '& .MuiDrawer-paper': {
    backgroundColor: '#FBFBFB',
    width: drawerWidth,
    zIndex: theme.zIndex.drawer + 2,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: 'hidden'
  }
}));

interface IDrawerProps {
  /**
   * The view that needs to be shown in the drawer (PPG or SKU)
   */
  viewType: DRAWER_VIEW_TYPE;
  /**
   * Controls the open state of the drawer
   */
  open: boolean;
  isFooterVisible: boolean;
  // eslint-disable-next-line react/require-default-props
  onClose?: (isSummaryRefreshRequired: boolean) => void;
}

/**
 * A switch option to render the content based on the given view
 */
function SwitchRender({ viewType }: { viewType: DRAWER_VIEW_TYPE }) {
  switch (viewType) {
    case DRAWER_VIEW_TYPE.PRODUCT_DETAILS:
      return <ProductDetailsDrawer />;

    case DRAWER_VIEW_TYPE.COMMENTS:
      return <BulkCommentsDrawer />;

    case DRAWER_VIEW_TYPE.TABLE_COMMENT_BOX:
      return <CommentBoxContainer />;

    default:
      return <div />;
  }
}

/**
 * Drawer reset button
 */
export function OpportunityResetActionButton({
  onReset: onDrawerReset,
  disabled
}: {
  /**
   * Disabled state control for reset button
   */
  disabled: boolean;
  /**
   * Handler for when user selects the option of reset
   * @param resetAll Boolean indicating whether everything needs to be reset or only opportunities
   * @returns void
   */
  onReset: (resetAll: boolean) => void;
}) {
  const { planner: plannerAccessConfig = {} } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const { isMenuOpen, anchorEl, handleMenuOpen, handleMenuClose } = useMenu();
  const handleOptionSelection = (resetAll: boolean) => {
    handleMenuClose();
    onDrawerReset(resetAll);
  };
  return (
    <>
      <Button disabled={disabled} onClick={handleMenuOpen}>
        Reset
      </Button>
      <Menu
        id='opportunity-reset-menu'
        sx={{ p: 1.5 }}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <MenuItem
          disabled={!plannerAccessConfig?.reset_own_comments}
          onClick={() => handleOptionSelection(true)}
        >
          <Typography fontSize='14px'>
            Reset Comments + Opportunities
          </Typography>
        </MenuItem>
        <MenuItem
          disabled={!plannerAccessConfig?.reset_own_opportunities}
          onClick={() => handleOptionSelection(false)}
        >
          <Typography fontSize='14px'>Reset only Opportunities</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * The base view of the drawer in which the content will be rendered
 * based on the given view and footer
 */
export default function DynamicDrawerBase({
  viewType,
  open,
  isFooterVisible = true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClose = () => { }
}: IDrawerProps) {
  const appDispatch = useAppDispatch();
  const { source: sourceId } = Storage.getSourceId();
  const { id: scenarioId } = useParams();
  const { moduleNameToModuleIdMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  const { isLoading, isSubmittingDrawerData, drawerData, productKeyRef } =
    useAppSelector((state) => state.plannerV2.drawerViewMetadata);

  const handleClose = (isSummaryRefreshRequired: boolean) => {
    appDispatch(
      toggleDrawerView({
        view: null,
        productId: null,
        productKeyRef: ''
      })
    );
    onClose(isSummaryRefreshRequired);
  };

  const submitOpportunities = () => {
    const drawerPayload: { prod_details: { [key: string]: string }[] } =
      createPayloadForOpportunitySubmissionV2(
        drawerData.drawer_data,
        Object.keys(drawerData.level_mapping || {})
      );
    appDispatch(
      submitOpportunitiesForProduct({
        moduleId: moduleNameToModuleIdMap.planner,
        payload: drawerPayload.prod_details,
        scenarioId: Number(scenarioId),
        sourceId
      })
    ).then(() => {
      handleClose(true);
    });
  };

  const handleDrawerReset = (resetAll: boolean) => {
    let payload: {
      id: number;
      key: string;
      resetAll: boolean;
    } = {
      id: -1,
      key: '',
      resetAll: false
    };
    drawerData.drawer_data.forEach((field: DrawerProductItem) => {
      payload = {
        id: field.parent.data[productKeyRef],
        key: productKeyRef,
        resetAll
      };
    });
    appDispatch(
      resetDrawerDetails({
        moduleId: moduleNameToModuleIdMap.planner,
        scenarioId: Number(scenarioId),
        sourceId,
        payload
      })
    );
  };

  return (
    <Backdrop
      data-testid='drawer-backdrop'
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
      onClick={() => handleClose(false)}
    >
      <Drawer
        onClick={(e) => {
          // To prevent bubbling of event towards backdrop which in turn will trigger close action
          e.preventDefault();
          e.stopPropagation();
        }}
        variant='persistent'
        open={open}
        anchor='right'
        onClose={() => handleClose(false)}
      >
        <DrawerContent>
          <Grid container>
            <Grid
              container
              item
              xs={12}
              sx={{ p: (theme) => theme.spacing(0, 1.5, 1.5) }}
            >
              <Grid item xs={12}>
                <SwitchRender viewType={viewType} />
              </Grid>
            </Grid>
            {DRAWER_VIEW_TYPE.COMMENTS === viewType ? null : !isLoading &&
              isFooterVisible ? (
              <Grid
                container
                justifyContent='space-between'
                item
                xs={12}
                sx={{
                  backgroundColor: 'white',
                  position: 'sticky',
                  p: 1.5,
                  bottom: 0,
                  boxShadow: (theme) => theme.shadows[1]
                }}
              >
                {isSubmittingDrawerData ? (
                  <Grid item xs={12}>
                    <LinearProgress />
                  </Grid>
                ) : null}
                <OpportunityResetActionButton
                  disabled={isSubmittingDrawerData}
                  onReset={handleDrawerReset}
                />
                <Button
                  variant='contained'
                  color='primary'
                  // disabled={isApplyButtonDisabled()}
                  onClick={submitOpportunities}
                >
                  Apply
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </DrawerContent>
      </Drawer>
    </Backdrop>
  );
}
